// src/features/landing/presentation/components/PremiumJobListingsSection.jsx
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useJobPosts } from "../../application/useJobPosts";


function getStatusDisplay(status) {
    switch (status) {
        case "OPEN":
            return "상시채용";
        case "COMPLETE":
            return "채용완료";
        case "CLOSED":
            return "채용마감";
        case "PENDING":
            return "대기중";
        default:
            return "상시채용";
    }
}

function getStatusClass(status) {
    switch (status) {
        case "COMPLETE":
            return "status-complete";
        case "CLOSED":
            return "status-closed";
        default:
            return "status-open";
    }
}


function getSalaryDisplay(min, max) {
    const isMinNegotiable = !min || min === "협의";
    const isMaxNegotiable = !max || max === "협의";

    if (isMinNegotiable && isMaxNegotiable) return "면접후결정";
    if (isMinNegotiable) return `${max} 이하`;
    if (isMaxNegotiable) return `${min} 이상`;
    if (min === max) return min;
    return `${min} ~ ${max}`;
}

export function PremiumJobListingsSection({ filters = {} }) {
    const navigate = useNavigate();
    const { jobPosts, loading, error, setFilters } = useJobPosts(8, 0);

    useEffect(() => {
        setFilters(filters);
    }, [filters, setFilters]);

    const jobs = jobPosts.map((post) => ({
        id: post.id,
        company: post.companyName,
        jobType: post.jobType || "채용",
        location: [post.companySido, post.companySigungu].filter(Boolean).join(" ") || "전국",
        salary: getSalaryDisplay(post.salaryRangeMin, post.salaryRangeMax),
        statusLabel: getStatusDisplay(post.status),
        statusClass: getStatusClass(post.status),
        isComplete: post.status === 'COMPLETE',
    }));

    const placeholderCount = Math.max(0, 8 - jobs.length);
    const placeholderJobs = Array.from({ length: placeholderCount }, (_, i) => ({
        id: `placeholder-${i}`,
    }));

    if (loading) {
        return (
            <section className="premium-job-listings-section">
                <div className="listings-container">
                    <div className="section-header">
                        <h2 className="section-title">
                            <span className="title-premium">프리미엄</span> 채용정보
                        </h2>
                    </div>
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>채용정보를 불러오는 중...</p>
                    </div>
                </div>
                <style jsx>{`
                    .premium-job-listings-section {
                        width: 100%;
                        background: #f8f9fa;
                        padding: 80px 40px;
                    }
                    .listings-container {
                        max-width: 1400px;
                        margin: 0 auto;
                    }
                    .section-header {
                        margin-bottom: 50px;
                    }
                    .section-title {
                        font-size: 2.2rem;
                        font-weight: 700;
                        color: #2c3e50;
                        margin: 0;
                    }
                    .title-premium {
                        color: #f16102;
                    }
                    .loading-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 80px 0;
                        color: #6c757d;
                    }
                    .loading-spinner {
                        width: 40px;
                        height: 40px;
                        border: 3px solid #e9ecef;
                        border-top-color: #f16102;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 16px;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </section>
        );
    }

    if (error) {
        return (
            <section className="premium-job-listings-section">
                <div className="listings-container">
                    <div className="section-header">
                        <h2 className="section-title">
                            <span className="title-premium">프리미엄</span> 채용정보
                        </h2>
                    </div>
                    <div className="notice-container">
                        <div className="notice-icon">📋</div>
                        <p className="notice-title">채용정보 준비 중</p>
                        <p className="notice-description">현재 채용정보를 준비하고 있습니다.<br/>잠시 후 다시 확인해 주세요.</p>
                    </div>
                </div>
                <style jsx>{`
                    .premium-job-listings-section {
                        width: 100%;
                        background: #f8f9fa;
                        padding: 80px 40px;
                    }
                    .listings-container {
                        max-width: 1400px;
                        margin: 0 auto;
                    }
                    .section-header {
                        margin-bottom: 50px;
                    }
                    .section-title {
                        font-size: 2.2rem;
                        font-weight: 700;
                        color: #2c3e50;
                        margin: 0;
                    }
                    .title-premium {
                        color: #f16102;
                    }
                    .notice-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 80px 20px;
                        text-align: center;
                    }
                    .notice-icon {
                        font-size: 3rem;
                        margin-bottom: 16px;
                    }
                    .notice-title {
                        font-size: 1.3rem;
                        font-weight: 600;
                        color: #495057;
                        margin: 0 0 12px 0;
                    }
                    .notice-description {
                        font-size: 1rem;
                        color: #868e96;
                        margin: 0;
                        line-height: 1.6;
                    }
                `}</style>
            </section>
        );
    }

    return (
        <section className="premium-job-listings-section">
            <div className="listings-container">
                <div className="section-header">
                    <h2 className="section-title">
                        <span className="title-premium">프리미엄</span> 채용정보
                    </h2>
                    <div className="nav-banner">
                        <div className="nav-card nav-card--apply" onClick={() => navigate('/premium/apply')}>
                            <div className="nav-card-icon">📝</div>
                            <div className="nav-card-body">
                                <p className="nav-card-title">구직 신청</p>
                                <p className="nav-card-desc">보험 업계 취업을 원하시나요?<br/>인재등록 서비스를 신청하세요.</p>
                            </div>
                            <span className="nav-card-arrow">→</span>
                        </div>
                        <div className="nav-card nav-card--company" onClick={() => navigate('/premium/company')}>
                            <div className="nav-card-icon">🏢</div>
                            <div className="nav-card-body">
                                <p className="nav-card-title">구인 신청</p>
                                <p className="nav-card-desc">우수 인재를 찾고 계신가요?<br/>프리미엄 채용 서비스를 신청하세요.</p>
                            </div>
                            <span className="nav-card-arrow">→</span>
                        </div>
                    </div>
                </div>

                <div className="jobs-grid">
                    {jobs.map((job) => (
                        <div key={job.id} className={`job-card${job.isComplete ? ' job-card-complete' : ''}`} onClick={() => navigate(`/jobs/${job.id}`)}>
                            {job.isComplete && (
                                <div className="complete-stamp">COMPLETED</div>
                            )}
                            <div className="job-header">
                                {job.isComplete && <div className="complete-check-icon">✓</div>}
                                <h3 className="company-name">{job.company}</h3>
                            </div>

                            <div className="job-info">
                                <div className="info-row">
                                    <span className="info-label">직무</span>
                                    <span className="info-value">{job.jobType}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">지역</span>
                                    <span className="info-value">{job.location}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">급여</span>
                                    <span className="info-value">{job.salary}</span>
                                </div>
                            </div>

                            <div className="job-footer">
                                <span className={`job-status ${job.statusClass}`}>
                                    {job.statusLabel}
                                </span>
                            </div>
                        </div>
                    ))}

                    {placeholderJobs.map((placeholder) => (
                        <div key={placeholder.id} className="job-card placeholder-card">
                            <div className="placeholder-content">
                                <div className="placeholder-icon">
                                    <div className="monitor-icon">
                                        <div className="monitor-screen">
                                            <span className="plus-icon">+</span>
                                        </div>
                                        <div className="monitor-stand"></div>
                                    </div>
                                </div>
                                <p className="placeholder-text">채용광고</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="view-more-container">
                    <button className="view-more-button" onClick={() => navigate('/jobs')}>
                        채용정보 더보기
                    </button>
                </div>
            </div>

            <style jsx>{`
                .premium-job-listings-section {
                    width: 100%;
                    background: #f8f9fa;
                    padding: 80px 40px;
                }

                .listings-container {
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .section-header {
                    margin-bottom: 50px;
                }

                .nav-banner {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    margin-top: 28px;
                }

                .nav-card {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    background: white;
                    border: 1.5px solid #e2e8f0;
                    border-left: 4px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 24px 28px;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                }

                .nav-card--apply {
                    border-left-color: #f16102;
                }

                .nav-card--company {
                    border-left-color: #2c5282;
                }

                .nav-card:hover {
                    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }

                .nav-card--apply:hover {
                    border-color: #f16102;
                    border-left-color: #f16102;
                }

                .nav-card--company:hover {
                    border-color: #2c5282;
                    border-left-color: #2c5282;
                }

                .nav-card--apply:hover .nav-card-title {
                    color: #f16102;
                }

                .nav-card--company:hover .nav-card-title {
                    color: #2c5282;
                }

                .nav-card-icon {
                    font-size: 2rem;
                    flex-shrink: 0;
                    width: 56px;
                    height: 56px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .nav-card--apply .nav-card-icon {
                    background: #fff4ec;
                }

                .nav-card--company .nav-card-icon {
                    background: #ebf4ff;
                }

                .nav-card-body {
                    flex: 1;
                }

                .nav-card-title {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0 0 6px 0;
                    transition: color 0.2s;
                }

                .nav-card-desc {
                    font-size: 0.9rem;
                    color: #4a5568;
                    margin: 0;
                    line-height: 1.65;
                    font-weight: 400;
                }

                .nav-card-arrow {
                    font-size: 1.3rem;
                    font-weight: 700;
                    flex-shrink: 0;
                    transition: transform 0.2s;
                }

                .nav-card--apply .nav-card-arrow {
                    color: #f16102;
                }

                .nav-card--company .nav-card-arrow {
                    color: #2c5282;
                }

                .nav-card:hover .nav-card-arrow {
                    transform: translateX(4px);
                }

                .view-more-container {
                    display: flex;
                    justify-content: center;
                    margin-top: 40px;
                }

                .section-title {
                    font-size: 2.2rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0;
                }

                .title-premium {
                    color: #f16102;
                }

                .view-more-button {
                    background: white;
                    border: 2px solid #dee2e6;
                    border-radius: 8px;
                    padding: 12px 28px;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #495057;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .view-more-button:hover {
                    background: #f8f9fa;
                    border-color: #f16102;
                    color: #f16102;
                }

                .jobs-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 25px;
                }

                .job-card {
                    position: relative;
                    background: white;
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                    padding: 32px 28px;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    overflow: hidden;
                }

                .complete-check-icon {
                    width: 27px;
                    height: 25px;
                    flex-shrink: 0;
                    border-radius: 50%;
                    border: 3px solid rgba(185, 28, 28, 0.8);
                    color: rgba(185, 28, 28, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    font-weight: 900;
                    pointer-events: none;
                }

                .complete-stamp {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(10deg);
                    border: 3px solid rgb(200 0 0 / 80%);
                    color: rgb(200 0 0 / 80%);
                    font-size: 1.25rem;
                    font-weight: 900;
                    letter-spacing: 3px;
                    padding: 5px 25px;
                    border-radius: 4px;
                    pointer-events: none;
                    white-space: nowrap;
                    z-index: 2;
                }

                .job-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    border-color: #f16102;
                }

                .job-header {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding-bottom: 16px;
                }

                .company-name {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #2c3e50;
                    margin: 0;
                    line-height: 1.4;
                }

                .job-info {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                    flex: 1;
                }

                .info-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .info-label {
                    font-size: 0.85rem;
                    color: #adb5bd;
                    font-weight: 500;
                    min-width: 48px;
                }

                .info-value {
                    text-align: left;
                    font-size: 0.95rem;
                    color: #343a40;
                    font-weight: 600;
                    flex: 1;
                }

                .job-footer {
                    display: flex;
                    justify-content: flex-end;
                    padding-top: 16px;
                    border-top: 1px solid #e9ecef;
                }

                .job-status {
                    font-size: 0.85rem;
                    font-weight: 600;
                    padding: 6px 14px;
                    border-radius: 6px;
                }

                .status-open {
                    background: #d3f9d8;
                    color: #2b8a3e;
                }

                .status-closed {
                    background: #ffe8cc;
                    color: #e8590c;
                }

                .status-complete {
                    background: #e9ecef;
                    color: #495057;
                }

                .job-card-complete {
                    opacity: 0.72;
                    background: #f8f9fa;
                }

                .job-card-complete:hover {
                    border-color: #adb5bd;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.07);
                }


                .placeholder-card {
                    background: #f8f9fa;
                    border: 2px dashed #dee2e6;
                    justify-content: center;
                    align-items: center;
                    min-height: 280px;
                }

                .placeholder-card:hover {
                    transform: none;
                    box-shadow: none;
                    border-color: #dee2e6;
                }

                .placeholder-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 16px;
                }

                .placeholder-icon {
                    opacity: 0.4;
                }

                .monitor-icon {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .monitor-screen {
                    width: 100px;
                    height: 70px;
                    background: #dee2e6;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #adb5bd;
                }

                .plus-icon {
                    font-size: 1.3rem;
                }

                .number-icon {
                    font-size: 2rem;
                }

                .monitor-stand {
                    width: 40px;
                    height: 8px;
                    background: #dee2e6;
                    border-radius: 4px;
                    margin-top: 8px;
                }

                .placeholder-text {
                    font-size: 1rem;
                    color: #adb5bd;
                    font-weight: 600;
                    margin: 0;
                }

                @media (max-width: 1200px) {
                    .jobs-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (max-width: 900px) {
                    .jobs-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 600px) {
                    .premium-job-listings-section {
                        padding: 40px 16px;
                    }

                    .section-header {
                        margin-bottom: 30px;
                    }

                    .section-title {
                        font-size: 1.5rem;
                    }

                    .nav-banner {
                        grid-template-columns: 1fr;
                        gap: 12px;
                        margin-top: 20px;
                    }

                    .nav-card {
                        padding: 18px 20px;
                        gap: 16px;
                    }

                    .nav-card-icon {
                        width: 44px;
                        height: 44px;
                        font-size: 1.6rem;
                    }

                    .nav-card-title {
                        font-size: 1rem;
                    }

                    .nav-card-desc {
                        font-size: 0.875rem;
                    }

                    .view-more-button {
                        padding: 14px 64px;
                        font-size: 0.95rem;
                    }

                    .jobs-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .job-card {
                        padding: 24px 20px;
                        gap: 16px;
                    }

                    .job-header {
                        gap: 6px;
                        padding-bottom: 12px;
                    }

                    .company-name {
                        font-size: 1.1rem;
                    }

                    .job-info {
                        gap: 10px;
                    }

                    .info-label {
                        font-size: 0.8rem;
                        min-width: 28px;
                    }

                    .info-value {
                        font-size: 0.9rem;
                    }

                    .job-footer {
                        padding-top: 12px;
                    }

                    .job-status {
                        font-size: 0.8rem;
                        padding: 5px 12px;
                    }

                    .placeholder-card {
                        min-height: 180px;
                    }

                    .monitor-screen {
                        width: 80px;
                        height: 55px;
                    }
                }
            `}</style>
        </section>
    );
}
